import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useConfirm } from '@/composables/useConfirm';
import { nazwyNiezapisanych, saNiezapisaneZmiany, zapomnijNiezapisaneZmiany } from '@/composables/useNiezapisaneZmiany';

const routes = [
    // Auth
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { guest: true },
    },
    {
        path: '/login/sso-callback',
        name: 'sso.callback',
        component: () => import('@/views/auth/SsoCallbackView.vue'),
        meta: { public: true },
    },
    {
        path: '/forgot-password',
        name: 'password.forgot',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: { guest: true },
    },
    {
        // `public`, a nie `guest`: odnośnik z wiadomości ma zadziałać także
        // wtedy, gdy w przeglądarce wisi jeszcze cudza albo własna stara sesja.
        path: '/reset-password',
        name: 'password.reset',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: { public: true },
    },

    // Franchisee
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/views/franchisee/DashboardView.vue'),
        meta: { title: 'Panel', auth: true },
    },

    // Franchisee — onboarding
    {
        path: '/onboarding',
        name: 'onboarding',
        component: () => import('@/views/franchisee/OnboardingView.vue'),
        // `fill`: widok wypełnia wysokość obszaru treści (akordeon etapów
        // przewija się własnym kontenerem). Wyjątek układu należy do TRASY,
        // tak samo jak `wide` (DESIGN.md).
        meta: { title: 'Wdrożenie', auth: true, fill: true },
    },

    // Franchisee — overlay editor
    {
        path: '/overlay-editor',
        name: 'overlay-editor',
        component: () => import('@/views/franchisee/OverlayEditorView.vue'),
        meta: { title: 'SM Overlay', auth: true, overlayEnabled: true },
    },


    // Franchisee — files
    {
        path: '/files',
        name: 'files',
        component: () => import('@/views/franchisee/FileExplorerView.vue'),
        meta: { title: 'Eksplorator plików', auth: true, wide: true },
    },
    {
        path: '/files/:id/download',
        name: 'files.download',
        component: () => import('@/views/franchisee/FileDownloadView.vue'),
        meta: { auth: true },
    },

    // HQ
    {
        path: '/hq',
        name: 'hq.dashboard',
        component: () => import('@/views/hq/HQDashboardView.vue'),
        meta: { title: 'Panel Platformy', auth: true, can: 'hqStats.view' },
    },
    {
        path: '/hq/franchisees',
        name: 'hq.franchisees',
        component: () => import('@/views/hq/FranchiseeListView.vue'),
        meta: { title: 'Franczyzobiorcy', auth: true, can: 'franchisees.viewAll' },
    },
    {
        path: '/hq/franchisees/:id',
        name: 'hq.franchisees.show',
        component: () => import('@/views/hq/FranchiseeDetailView.vue'),
        meta: { title: 'Franczyzobiorca', parent: 'hq.franchisees', auth: true, can: 'franchisees.viewAll' },
    },
    {
        path: '/hq/branches/:id',
        name: 'hq.branches.show',
        component: () => import('@/views/hq/BranchDetailView.vue'),
        meta: { title: 'Oddział', parent: 'hq.branches', auth: true, can: 'branches.viewAll' },
    },
    {
        path: '/hq/branches/:id/files',
        name: 'hq.branches.files',
        component: () => import('@/views/hq/HQBranchFilesView.vue'),
        meta: { title: 'Pliki oddziału', auth: true, can: 'branches.viewAll', wide: true },
    },
    {
        path: '/hq/branches',
        name: 'hq.branches',
        component: () => import('@/views/hq/BranchListView.vue'),
        meta: { title: 'Oddziały', auth: true, can: 'branches.viewAll' },
    },
    // Aktywności całej sieci — odpowiednik widoku zbiorczego wątków nadzoru,
    // tyle że dla bieżącej współpracy z oddziałami.
    {
        path: '/hq/activities',
        name: 'hq.activities',
        component: () => import('@/views/hq/SalesActivitiesView.vue'),
        meta: { title: 'Aktywności handlowe', auth: true, can: 'crm.manage' },
    },
    {
        path: '/hq/crm',
        name: 'hq.crm',
        component: () => import('@/views/hq/CrmLeadListView.vue'),
        meta: { title: 'CRM — Leady', auth: true, can: 'crm.manage' },
    },
    {
        path: '/hq/crm/dashboard',
        name: 'hq.crm.dashboard',
        component: () => import('@/views/hq/CrmDashboardView.vue'),
        meta: { title: 'Panel rekrutacji', auth: true, can: 'crm.manage' },
    },
    {
        path: '/hq/crm/:id',
        name: 'hq.crm.lead',
        component: () => import('@/views/hq/CrmLeadDetailView.vue'),
        meta: { title: 'Lead', parent: 'hq.crm', auth: true, can: 'crm.manage' },
    },
    {
        path: '/hq/files',
        name: 'hq.files',
        component: () => import('@/views/hq/HQGlobalFilesView.vue'),
        // `wide`: ekran warsztatowy — trzy kolumny obok siebie plus panel
        // szczegółów. Limit powłoki ściskał tu drzewo teczek (decyzja 2026-08-13).
        meta: { title: 'Pliki sieci', auth: true, can: 'files.viewAll', wide: true },
    },
    // ——— Ustawienia systemu: jedna powłoka, trzy zakładki ———
    {
        path: '/hq/settings',
        component: () => import('@/views/hq/SettingsHubView.vue'),
        meta: { title: 'Ustawienia systemu', auth: true },
        children: [
            { path: '', name: 'hq.settings' },
            {
                path: 'uploads',
                name: 'hq.allowed-file-types',
                component: () => import('@/views/hq/AllowedFileTypesView.vue'),
                meta: { auth: true, can: 'files.manage' },
            },
            {
                path: 'document-types',
                name: 'hq.document-types',
                component: () => import('@/views/hq/DocumentTypesView.vue'),
                meta: { auth: true, can: 'templates.content' },
            },
            {
                path: 'overlay-formats',
                name: 'hq.overlayFormats',
                component: () => import('@/views/hq/OverlayFormatsView.vue'),
                meta: { auth: true, can: 'templates.content' },
            },
            {
                path: 'crm',
                name: 'hq.crmSettings',
                component: () => import('@/views/hq/CrmSettingsView.vue'),
                meta: { auth: true, can: 'templates.content' },
            },
        ],
    },
    { path: '/hq/allowed-file-types', redirect: '/hq/settings/uploads' },
    { path: '/hq/document-types',     redirect: '/hq/settings/document-types' },
    { path: '/hq/overlay-formats',    redirect: '/hq/settings/overlay-formats' },

    // ——— Konta i dostęp: jedna powłoka, dwie zakładki ———
    // Bramki zakładek są różne (`users.manage` / `roles.manage`), więc
    // pierwszą dostępną wybiera powłoka, nie `redirect` w trasie.
    {
        path: '/hq/access',
        component: () => import('@/views/hq/AccessHubView.vue'),
        meta: { title: 'Konta i dostęp', auth: true },
        children: [
            { path: '', name: 'hq.access' },
            {
                path: 'users',
                name: 'hq.users',
                component: () => import('@/views/hq/UserListView.vue'),
                meta: { auth: true, can: 'users.manage' },
            },
            {
                path: 'roles',
                name: 'hq.roles',
                component: () => import('@/views/hq/RoleManagerView.vue'),
                meta: { auth: true, can: 'roles.manage' },
            },
        ],
    },
    // Stare adresy mogą siedzieć w zakładkach przeglądarki — odsyłamy je
    // na nowe miejsce zamiast pokazywać pustkę.
    { path: '/hq/users', redirect: '/hq/access/users' },
    { path: '/hq/roles', redirect: '/hq/access/roles' },
    // ——— Szablony: jedna powłoka, sześć zakładek ———
    // Zakładki przełączają wyłącznie listy. Trasy `/:id` i `/:id/edit` zostają
    // poza powłoką (niżej, płasko) — otwarcie pozycji to zejście o poziom,
    // a nie zmiana zakładki.
    {
        path: '/hq/templates',
        component: () => import('@/views/hq/TemplatesHubView.vue'),
        meta: { title: 'Szablony', auth: true },
        children: [
            // Bez `redirect` w trasie: przy zimnym wejściu uprawnienia nie są
            // jeszcze wczytane, więc pierwszą DOSTĘPNĄ zakładkę wybiera powłoka.
            { path: '', name: 'hq.templates' },
            {
                path: 'onboarding-programs',
                name: 'hq.templates.onboardingPrograms',
                component: () => import('@/views/hq/ProgramListView.vue'),
                meta: { auth: true, can: 'templates.onboarding' },
            },
            {
                path: 'evaluation-programs',
                name: 'hq.templates.evaluationPrograms',
                component: () => import('@/views/hq/EvaluationProgramListView.vue'),
                meta: { auth: true, can: 'templates.onboarding' },
            },
            {
                path: 'folder-structure',
                name: 'hq.templates.folderStructure',
                component: () => import('@/views/hq/TemplateManagerView.vue'),
                meta: { auth: true, can: 'templates.content' },
            },
            {
                path: 'instructions',
                name: 'hq.templates.instructions',
                component: () => import('@/views/hq/HQInstructionTemplatesView.vue'),
                meta: { auth: true, can: 'templates.content' },
            },
            {
                path: 'audits',
                name: 'hq.templates.audits',
                component: () => import('@/views/hq/AuditTemplateListView.vue'),
                meta: { auth: true, can: 'templates.onboarding' },
            },
            {
                path: 'analyses',
                name: 'hq.templates.analyses',
                component: () => import('@/views/hq/StrategicTemplateListView.vue'),
                meta: { auth: true, can: 'assessments.manage' },
            },
        ],
    },

    // Stare adresy list mogły trafić do zakładek przeglądarki — przekierowania
    // zostają na stałe, kosztują sześć wpisów.
    { path: '/hq/programs',              redirect: '/hq/templates/onboarding-programs' },
    { path: '/hq/evaluation-programs',   redirect: '/hq/templates/evaluation-programs' },
    { path: '/hq/instruction-templates', redirect: '/hq/templates/instructions' },
    { path: '/hq/audit-templates',       redirect: '/hq/templates/audits' },
    { path: '/hq/strategic-templates',   redirect: '/hq/templates/analyses' },

    {
        path: '/hq/strategic-templates/:id',
        name: 'hq.strategicTemplates.show',
        component: () => import('@/views/hq/StrategicTemplatePreviewView.vue'),
        meta: { title: 'Szablon analizy — podgląd', auth: true, can: 'assessments.manage' },
    },
    {
        path: '/hq/strategic-templates/:id/edit',
        name: 'hq.strategic-templates.edit',
        component: () => import('@/views/hq/StrategicTemplateEditorView.vue'),
        meta: { title: 'Edytor szablonu analizy', auth: true, can: 'templates.onboarding' },
    },
    {
        path: '/hq/strategic-assessments',
        name: 'hq.strategic-assessments',
        component: () => import('@/views/hq/StrategicAssessmentListView.vue'),
        meta: { title: 'Analiza strategiczna', auth: true, can: 'assessments.manage' },
    },
    {
        path: '/hq/strategic-assessments/new',
        name: 'hq.strategic-assessments.new',
        component: () => import('@/views/hq/StrategicAssessmentFormView.vue'),
        meta: { parent: 'hq.strategic-assessments', title: 'Analiza strategiczna oddziału', auth: true, can: 'assessments.manage' },
    },
    {
        path: '/hq/strategic-assessments/:id/edit',
        name: 'hq.strategic-assessments.edit',
        component: () => import('@/views/hq/StrategicAssessmentFormView.vue'),
        meta: { parent: 'hq.strategic-assessments', title: 'Analiza strategiczna oddziału', auth: true, can: 'assessments.manage' },
    },
    {
        path: '/hq/strategic-assessments/:id',
        name: 'hq.strategic-assessments.show',
        component: () => import('@/views/hq/StrategicAssessmentPreviewView.vue'),
        meta: { parent: 'hq.strategic-assessments', title: 'Analiza strategiczna — podgląd', auth: true, can: 'assessments.manage' },
    },
    {
        path: '/hq/programs/:id',
        name: 'hq.programs.show',
        component: () => import('@/views/hq/ProgramPreviewView.vue'),
        meta: { title: 'Program wdrożenia', parent: 'hq.templates.onboardingPrograms', auth: true, can: 'templates.onboarding' },
    },
    {
        path: '/hq/programs/:id/edit',
        name: 'hq.programs.edit',
        component: () => import('@/views/hq/ProgramEditorView.vue'),
        meta: { title: 'Edycja programu', parent: 'hq.templates.onboardingPrograms', auth: true, can: 'templates.onboarding' },
    },
    {
        path: '/hq/evaluation-programs/:id',
        name: 'hq.evaluation-programs.show',
        component: () => import('@/views/hq/EvaluationProgramPreviewView.vue'),
        meta: { title: 'Program ocen', parent: 'hq.templates.evaluationPrograms', auth: true, can: 'templates.onboarding' },
    },
    {
        path: '/hq/evaluation-programs/:id/edit',
        name: 'hq.evaluation-programs.edit',
        component: () => import('@/views/hq/EvaluationProgramEditorView.vue'),
        meta: { title: 'Edycja programu ocen', parent: 'hq.templates.evaluationPrograms', auth: true, can: 'templates.onboarding' },
    },
    {
        path: '/hq/evaluations',
        name: 'hq.evaluations',
        component: () => import('@/views/hq/EvaluationListView.vue'),
        meta: { title: 'Ewaluacje oddziałów', auth: true, can: 'evaluations.manage' },
    },
    {
        path: '/hq/evaluations/:id',
        name: 'hq.evaluations.show',
        component: () => import('@/views/hq/EvaluationFormView.vue'),
        meta: { title: 'Ocena', parent: 'hq.evaluations', auth: true, can: 'evaluations.manage' },
    },
    {
        path: '/hq/evaluations/:id/review',
        name: 'hq.evaluations.review',
        component: () => import('@/views/hq/EvaluationReviewView.vue'),
        meta: { title: 'Przegląd oceny', parent: 'hq.evaluations', auth: true, can: 'evaluations.manage' },
    },
    {
        path: '/evaluations',
        name: 'evaluations',
        component: () => import('@/views/franchisee/EvaluationsView.vue'),
        meta: { title: 'Wyniki KPI', auth: true },
    },
    {
        path: '/evaluations/:id',
        name: 'evaluations.show',
        component: () => import('@/views/franchisee/EvaluationDetailView.vue'),
        meta: { title: 'Wynik KPI', parent: 'evaluations', auth: true },
    },

    {
        path: '/hq/overlay-templates',
        name: 'hq.overlay-templates',
        component: () => import('@/views/hq/OverlayTemplateManagerView.vue'),
        meta: { title: 'Nakładki graficzne', auth: true, can: 'templates.content' },
    },
    {
        path: '/hq/overlay-generations',
        name: 'hq.overlay-generations',
        component: () => import('@/views/hq/HQOverlayGenerationsView.vue'),
        meta: { title: 'Generowania Overlay', auth: true, can: 'templates.content' },
    },

    // Rejestry repozytorium — dwie zakładki w jednej pozycji menu: co wyszło
    // na zewnątrz (udostępnienia) i kto co pobierał. Bramki różne, więc
    // pierwszą dostępną zakładkę wybiera powłoka, nie `redirect` w trasie.
    {
        path: '/hq/file-registers',
        component: () => import('@/views/hq/FileRegistersHubView.vue'),
        meta: { title: 'Rejestry plików', auth: true },
        children: [
            { path: '', name: 'hq.fileRegisters' },
            {
                path: 'shares',
                name: 'hq.fileRegisters.shares',
                component: () => import('@/views/hq/ShareLinkRegisterView.vue'),
                meta: { auth: true, can: 'files.viewAll' },
            },
            {
                // Dane obejmują adresy IP, stąd auditLog.view zamiast files.viewAll
                path: 'downloads',
                name: 'hq.fileRegisters.downloads',
                component: () => import('@/views/hq/FileDownloadRegisterView.vue'),
                meta: { auth: true, can: 'auditLog.view' },
            },
            {
                path: 'handovers',
                name: 'hq.fileRegisters.handovers',
                component: () => import('@/views/hq/HandoverRegisterView.vue'),
                meta: { auth: true, can: 'files.viewAll' },
            },
        ],
    },

    // Nadrabianie zaległości — wejście z powiadomienia, nie z menu.
    // Sytuacja wyjątkowa (kilka razy w roku), więc stała pozycja w menu
    // pompowałaby je pod rzecz, której zwykle nie ma.
    {
        path: '/hq/handovers/backlog/:id',
        name: 'hq.handovers.backlog',
        component: () => import('@/views/hq/HandoverBacklogView.vue'),
        meta: { title: 'Zaległe materiały', auth: true, can: 'announcements.manage' },
    },

    // Stary adres rejestru pobrań — zakładki przejęły go 2026-08-12.
    // Zostaje, bo krąży po zakładkach w przeglądarkach i w bazie wiedzy.
    { path: '/hq/file-downloads', redirect: '/hq/file-registers/downloads' },

    // Rejestr zdarzeń — wpisy zawierają adresy IP, dostęp tylko dla Super Admina
    {
        path: '/hq/audit-log',
        name: 'hq.audit-log',
        component: () => import('@/views/hq/AuditLogView.vue'),
        meta: { title: 'Rejestr zdarzeń', auth: true, can: 'eventLog.view' },
    },

    // HQ Legal Documents
    {
        path: '/hq/documents',
        name: 'hq.documents',
        component: () => import('@/views/hq/HQDocumentsView.vue'),
        meta: { title: 'Dokumenty prawne', auth: true, can: 'files.manage' },
    },

    // HQ Calendar
    {
        path: '/hq/calendar',
        name: 'hq.calendar',
        component: () => import('@/views/hq/HQCalendarView.vue'),
        meta: { title: 'Kalendarz', auth: true, can: 'hqStats.view' },
    },

    // Surveys — Franchisee
    {
        path: '/surveys',
        name: 'surveys',
        component: () => import('@/views/franchisee/SurveysView.vue'),
        meta: { title: 'Ankiety', auth: true },
    },
    {
        path: '/surveys/:id',
        name: 'surveys.show',
        component: () => import('@/views/franchisee/SurveyFormView.vue'),
        meta: { title: 'Ankieta', parent: 'surveys', auth: true },
    },

    // Baza wiedzy — czytnik (wspólny dla franczyzobiorcy i HQ)
    {
        path: '/knowledge-base',
        name: 'knowledge-base',
        component: () => import('@/views/kb/KbReaderView.vue'),
        meta: { title: 'Baza wiedzy', auth: true },
    },

    // Wątki nadzoru — zbiorczo ze wszystkich obiektów. `comments.write`, nie
    // `crm.supervise`: opiekun też musi widzieć, gdzie na niego czekają.
    {
        path: '/hq/supervision',
        name: 'hq.supervision',
        component: () => import('@/views/hq/SupervisionThreadsView.vue'),
        meta: { title: 'Wątki nadzoru', auth: true, can: 'comments.write' },
    },

    // Ogłoszenia — HQ (lista store'owa + szczegół z read-stats)
    {
        path: '/hq/announcements',
        name: 'hq.announcements',
        component: () => import('@/views/hq/AnnouncementListView.vue'),
        // `wide`: lista ma sześć kolumn, a panel tworzenia wysuwa się z prawej —
        // limit 1440 px ściskał jedno i drugie (analiza 2026-08-14).
        meta: { title: 'Ogłoszenia', auth: true, can: 'announcements.manage', wide: true },
    },
    {
        path: '/hq/announcements/:id',
        name: 'hq.announcements.show',
        component: () => import('@/views/hq/AnnouncementDetailView.vue'),
        meta: { parent: 'hq.announcements', title: 'Ogłoszenie', auth: true, can: 'announcements.manage' },
    },

    // Ogłoszenia — franczyzobiorca
    {
        path: '/announcements',
        name: 'announcements',
        component: () => import('@/views/franchisee/AnnouncementsView.vue'),
        meta: { title: 'Ogłoszenia', auth: true },
    },

    // Baza wiedzy — HQ (ten sam czytnik, z afordancjami autora dla kb.manage).
    // Dwa uprawnienia, bo od 2026-08 odczyt treści wewnętrznych jest oddzielony
    // od redagowania: audytor czyta instrukcje do audytów, ale nic nie zmienia.
    {
        path: '/hq/knowledge-base',
        name: 'hq.knowledgeBase',
        component: () => import('@/views/kb/KbReaderView.vue'),
        meta: { title: 'Baza wiedzy', auth: true, can: ['kb.manage', 'kb.viewInternal'] },
    },
    {
        path: '/hq/knowledge-base/:id/edit',
        name: 'hq.knowledgeBase.edit',
        component: () => import('@/views/hq/KbArticleEditorView.vue'),
        meta: { parent: 'hq.knowledgeBase', title: 'Edycja artykułu', auth: true, can: 'kb.manage' },
    },

    // HQ Surveys
    {
        path: '/hq/surveys',
        name: 'hq.surveys',
        component: () => import('@/views/hq/SurveyListView.vue'),
        meta: { title: 'Ankiety', auth: true, can: 'surveys.manage' },
    },
    {
        path: '/hq/surveys/:id/edit',
        name: 'hq.surveys.edit',
        component: () => import('@/views/hq/SurveyEditorView.vue'),
        meta: { parent: 'hq.surveys', title: 'Edycja ankiety', auth: true, can: 'surveys.manage' },
    },
    {
        path: '/hq/surveys/:id/results',
        name: 'hq.surveys.results',
        component: () => import('@/views/hq/SurveyResultsView.vue'),
        meta: { parent: 'hq.surveys', title: 'Wyniki ankiety', auth: true, can: 'surveys.manage' },
    },

    // HQ Audit Templates
    {
        path: '/hq/audit-templates/:id',
        name: 'hq.auditTemplates.show',
        component: () => import('@/views/hq/AuditTemplatePreviewView.vue'),
        meta: { parent: 'hq.templates.audits', title: 'Szablon audytu — podgląd', auth: true, can: 'templates.onboarding' },
    },
    {
        path: '/hq/audit-templates/:id/edit',
        name: 'hq.auditTemplates.edit',
        component: () => import('@/views/hq/AuditTemplateEditorView.vue'),
        meta: { parent: 'hq.templates.audits', title: 'Edycja szablonu audytu', auth: true, can: 'templates.onboarding' },
    },

    // HQ Field Audits — audytor ma tu dostęp (audits.conduct)
    {
        path: '/hq/field-audits',
        name: 'hq.fieldAudits',
        component: () => import('@/views/hq/FieldAuditListView.vue'),
        meta: { title: 'Audyty terenowe', auth: true, can: 'audits.conduct' },
    },
    {
        path: '/hq/field-audits/:id',
        name: 'hq.fieldAudits.show',
        component: () => import('@/views/hq/FieldAuditFormView.vue'),
        meta: { title: 'Audyt terenowy', parent: 'hq.fieldAudits', auth: true, can: 'audits.conduct' },
    },

    // Franchisee Surveys

    // Public
    {
        path: '/s/:token',
        name: 'public.share',
        component: () => import('@/views/public/PublicShareView.vue'),
        meta: { public: true },
    },

    // Profile (all authenticated users)
    {
        path: '/profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { title: 'Mój profil', auth: true },
    },

    // Redirects
    { path: '/', redirect: '/dashboard' },
    { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
];

/**
 * Trasy, których widoki zdjęły własną powłokę (plan „layout poza RouterView").
 *
 * UWAGA: powłoka należy do TRASY, a widok bywa użyty przez KILKA tras
 * (np. czytnik bazy wiedzy obsługuje `/knowledge-base` i `/hq/knowledge-base`).
 * Pominięcie choćby jednej z nich daje ekran bez menu — widok nie ma już
 * własnej powłoki, a trasa jeszcze nie dostała wspólnej. Pilnuje tego test
 * `router-shell-coverage`.
 *
 * Powód: dotąd każdy widok sam opakowywał się w `AuthenticatedLayout`, więc przy
 * każdej nawigacji menu boczne i pasek górny były niszczone i montowane od nowa —
 * z resetem przewijania, zwiniętych grup i ponownym pobieraniem liczników.
 *
 * Migracja idzie porcjami, więc przez chwilę oba światy działają obok siebie:
 * ścieżki z tej listy wchodzą pod wspólną trasę-powłokę, reszta nadal opakowuje
 * się sama. Gdy lista obejmie wszystko, znika razem z tym mechanizmem.
 */
const ZE_WSPOLNA_POWLOKA = [
    '/onboarding',
    '/surveys/:id',
    '/hq/field-audits/:id',
    '/hq/programs/:id/edit',
    '/hq/programs/:id',
    '/hq/evaluation-programs/:id/edit',
    '/hq/evaluation-programs/:id',
    '/hq/evaluations/:id/review',
    '/hq/evaluations/:id',
    '/evaluations/:id',
    '/hq/franchisees/:id',
    '/hq/crm/:id',
    '/hq/branches/:id',
    '/surveys',
    '/hq/surveys',
    '/hq/supervision',
    '/hq/activities',
    '/announcements',
    '/dashboard',
    '/evaluations',
    '/files',
    '/hq',
    '/hq/allowed-file-types',
    '/hq/announcements',
    '/hq/announcements/:id',
    '/hq/audit-log',
    '/hq/audit-templates/:id',
    '/hq/audit-templates/:id/edit',
    '/hq/branches',
    '/hq/branches/:id/files',
    '/hq/calendar',
    '/hq/crm',
    '/hq/crm/dashboard',
    '/hq/documents',
    '/hq/evaluations',
    '/hq/field-audits',
    '/hq/access',
    '/hq/file-registers',
    '/hq/handovers/backlog/:id',
    '/hq/files',
    '/hq/franchisees',
    '/hq/knowledge-base',
    '/hq/knowledge-base/:id/edit',
    '/hq/overlay-generations',
    '/hq/overlay-templates',
    '/hq/settings',
    '/hq/strategic-assessments',
    '/hq/strategic-assessments/:id/edit',
    '/hq/strategic-assessments/:id',
    '/hq/strategic-assessments/new',
    '/hq/strategic-templates/:id',
    '/hq/strategic-templates/:id/edit',
    '/hq/surveys/:id/edit',
    '/hq/surveys/:id/results',
    '/hq/templates',
    '/knowledge-base',
    '/overlay-editor',
    '/profile',
];

// Ścieżki bezwzględne w dzieciach są celowe — dzięki nim zagnieżdżenie nie
// zmienia ani jednego adresu, więc migracja jest niewidoczna z zewnątrz.
const doPowloki = (r) => ZE_WSPOLNA_POWLOKA.includes(r.path);

const drzewoTras = [
    ...routes.filter((r) => !doPowloki(r)),
    {
        path: '',
        component: () => import('@/components/layout/AuthenticatedLayout.vue'),
        children: routes.filter(doPowloki),
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes: drzewoTras,
});

router.beforeEach(async (to, from) => {
    // Karta szczegółów zapisuje się sekcjami, a wpisane dane giną przy wyjściu
    // bez śladu — do 2026-08 nikt o tym nie ostrzegał (analiza:
    // `docs/analiza-zapis-na-kartach-szczegolow.md`). Pytanie idzie PRZED
    // sprawdzeniem uprawnień: odmowa dostępu i tak zabrałaby dane, a użytkownik
    // ma prawo najpierw wrócić i zapisać.
    if (from.name && to.path !== from.path && saNiezapisaneZmiany()) {
        const sekcje = nazwyNiezapisanych();
        const czego = sekcje.length ? ` (${sekcje.join(', ')})` : '';

        const { confirm } = useConfirm();
        const wyjsc = await confirm(
            `Masz niezapisane zmiany${czego}. Wyjście z tej strony je porzuci.`,
            { title: 'Porzucić niezapisane zmiany?', confirmLabel: 'Porzuć zmiany', destructive: true },
        );

        if (!wyjsc) return false;

        zapomnijNiezapisaneZmiany();
    }

    if (to.meta.public) return;

    const auth = useAuthStore();

    if (!auth.user && auth.token) {
        await auth.fetchMe();
    }

    if (to.meta.auth && !auth.isAuthenticated) {
        // Zachowaj docelowy adres (np. link do zadania /onboarding?task=…)
        return { name: 'login', query: { redirect: to.fullPath } };
    }

    // Landing zależny od uprawnień: dostęp do statystyk HQ = pulpit HQ,
    // inaczej pulpit franczyzobiorcy. Audytor ma hqStats.view → ląduje w HQ.
    const landing = auth.can('hqStats.view') ? 'hq.dashboard' : 'dashboard';

    if (to.meta.guest && auth.isAuthenticated) {
        return { name: landing };
    }

    // HQ nie ląduje na pulpicie franczyzobiorcy (root '/' → '/dashboard' na sztywno) — odbij na HQ
    if (to.name === 'dashboard' && auth.can('hqStats.view')) {
        return { name: 'hq.dashboard' };
    }

    // Trasa wymaga uprawnienia, którego user nie ma → odbij na jego landing.
    if (to.meta.can && !auth.can(to.meta.can)) {
        return { name: landing };
    }

    if (to.meta.overlayEnabled && !auth.isAdmin && !auth.overlayEnabled) {
        return { name: 'dashboard' };
    }
});

export default router;
